import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const reasons = [
  {
    icon: 'ri-award-fill',
    title: 'CSIA & NADCA Certified',
    desc: 'Our technicians hold certifications from the Chimney Safety Institute of America and the National Air Duct Cleaners Association — the gold standards in our industry.',
  },
  {
    icon: 'ri-shield-check-fill',
    title: 'Fully Licensed & Insured',
    desc: 'BBM Services is fully licensed in Florida and carries comprehensive liability insurance, protecting you and your property on every job we perform.',
  },
  {
    icon: 'ri-camera-3-line',
    title: 'Before & After Documentation',
    desc: 'We photograph every job before and after so you can see exactly what we cleaned and the quality of work delivered. No guesswork, no hidden surprises.',
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Flexible Scheduling',
    desc: 'We offer weekday and Saturday appointments across Jacksonville, with same-week availability for most services. We work around your schedule.',
  },
  {
    icon: 'ri-price-tag-3-line',
    title: 'Upfront, Transparent Pricing',
    desc: 'No surprise fees. We provide honest, upfront quotes before any work begins. What we quote is what you pay — always.',
  },
  {
    icon: 'ri-home-heart-line',
    title: 'Safety-First Approach',
    desc: 'Every service we perform is rooted in home safety. We don\'t just clean — we inspect, advise, and help protect your family from hidden hazards.',
  },
];

export default function WhyChooseUs() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="py-24 bg-brand-dark relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #D97706 0%, transparent 50%), radial-gradient(circle at 80% 50%, #D97706 0%, transparent 50%)' }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div ref={ref} className={`flex flex-col lg:flex-row gap-12 items-start mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="lg:w-2/5">
            <div className="inline-flex items-center gap-2 text-brand-amber text-xs font-bold uppercase tracking-widest mb-4">
              <div className="h-px w-8 bg-brand-amber" />
              Why BBM Services
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
              Jacksonville Homeowners Trust Us for a Reason
            </h2>
            <p className="text-white/60 text-lg leading-relaxed">
              We've served hundreds of Jacksonville families with the same commitment: certified expertise, transparent service, and results you can see.
            </p>
          </div>
          <div className="lg:w-3/5 flex items-center">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 w-full">
              {[
                { num: '500+', label: 'Five-Star Reviews' },
                { num: '15+', label: 'Years Experience' },
                { num: '3,000+', label: 'Homes Served' },
                { num: '100%', label: 'Satisfaction Rate' },
                { num: 'CSIA', label: 'Certified' },
                { num: 'NADCA', label: 'Certified' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-4 bg-white/5 rounded-xl border border-white/10">
                  <div className="font-serif text-3xl font-bold text-brand-amber mb-1">{stat.num}</div>
                  <div className="text-white/60 text-xs uppercase tracking-wide">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Reason cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reasons.map((reason, i) => (
            <div
              key={reason.title}
              className="p-6 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 hover:border-brand-amber/30 transition-all duration-300"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="w-11 h-11 flex items-center justify-center bg-brand-amber/20 rounded-lg mb-4">
                <i className={`${reason.icon} text-xl text-brand-amber`} />
              </div>
              <h3 className="font-semibold text-white text-lg mb-2">{reason.title}</h3>
              <p className="text-white/55 text-sm leading-relaxed">{reason.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
